/**
 * PATH: packages/ui/src/kanban/card-surface/contract.ts
 * FILE_ID: UI-KANBAN-CARDSURFACE-CONTRACT-001
 * PURPOSE: Define the required, view-independent card runtime contract.
 * WHY: Views may vary projection geometry, never silently drop card behavior.
 * FLOW: Card surface + runtime + instance → registered projection adapter.
 * DEPENDENCIES: model/normalizeCardWire.ts.
 * RELATED: registry.ts; rollout.ts; ../components/KanbanCard.types.ts.
 */
import type { CardSurfaceModel } from './model/normalizeCardWire'

export const CARD_SURFACE_IDS = [
  'board-lane-card',
  'ar-ap-lane-card',
  'time-bucket-card',
  'smart-time-card',
  'assignee-swimlane-card',
  'split-lane-card',
  'checklist-row',
  'table-row',
  'calendar-event',
  'calendar-unscheduled-card',
  'timeline-bar',
  'timeline-unscheduled-card',
  'planner-timeline-bar',
  'planner-reminder',
  'cashflow-row',
  'cashflow-dag-node',
  'my-cards-row',
  'notebook-source-reference',
  'calendar-action',
] as const

export type CardSurfaceId = (typeof CARD_SURFACE_IDS)[number]

export type CardSurfaceProjection =
  | 'tile'
  | 'checklist-row'
  | 'table-row'
  | 'calendar-event'
  | 'timeline-bar'
  | 'agenda-row'
  | 'financial-row'
  | 'financial-node'
  | 'reference'
  | 'multi-action'

export type CardMobileTier = 'full' | 'review' | 'desktop-redirect'

export const CARD_COMMAND_IDS = [
  'open',
  'open-context-menu',
  'toggle-complete',
  'rename',
  'set-priority',
  'set-action-date',
  'set-due-date',
  'set-followup-date',
  'assign',
  'move',
  'add-placement',
  'remove-placement',
  'archive',
  'restore',
  'delete',
  'pin',
  'unpin',
  'toggle-checklist',
  'quick-capture',
  'open-email',
  'allocate-email',
  'compose-email',
  'open-workbook',
  'open-flowchart',
  'open-entries',
  'open-thread',
  'create-comment',
  'nest',
  'promote-to-top-level',
  'copy-to-board',
  'duplicate',
  'snooze',
  'block',
  'schedule-meeting',
  'convert-to-notebook',
  'revert-last-action',
] as const

export type CardCommandId = (typeof CARD_COMMAND_IDS)[number]

export type CardCapabilityUnavailableReason =
  | 'permission'
  | 'read-only'
  | 'host-unsupported'
  | 'not-applicable'
  | 'loading'
  | 'contract-missing'

export type CardCommandInput = Readonly<Record<string, unknown>>

export interface CardCommandResult {
  actionId?: string
  message?: string
}

export interface AvailableCardCommand {
  availability: 'available'
  execute: (input: CardCommandInput) => Promise<CardCommandResult>
}

export interface UnavailableCardCommand {
  availability: 'unavailable'
  reason: CardCapabilityUnavailableReason
}

export type CardCommandCapability = AvailableCardCommand | UnavailableCardCommand

export type CardCommandManifest = Readonly<
  Record<CardCommandId, CardCommandCapability>
>

export interface CardSurfaceRuntimeIdentity {
  boardId: string | null
  currentUserId: string | number | null
}

interface CardSurfaceRuntimeBase {
  version: 1
  identity: Readonly<CardSurfaceRuntimeIdentity>
  commands: CardCommandManifest
}

export interface InteractiveCardSurfaceRuntime extends CardSurfaceRuntimeBase {
  mode: 'interactive'
}

export interface ReadOnlyCardSurfaceRuntime extends CardSurfaceRuntimeBase {
  mode: 'read-only'
}

export type CardSurfaceRuntime =
  | InteractiveCardSurfaceRuntime
  | ReadOnlyCardSurfaceRuntime

export interface CardSurfaceInstance {
  sourceBoardId: string | null
  laneId: string | null
  placementId: string | null
  treeDepth: number
  selected: boolean
  activeFindHit: boolean
}

export interface CardSurfaceDecoration {
  id: string
  placement: 'before-meta' | 'after-meta' | 'footer'
  visible: boolean
  kind: 'tenant-detail' | 'schedule-anchor' | 'hours-input' | 'custom'
}

export interface CardSurfaceContract {
  surfaceId: CardSurfaceId
  model: CardSurfaceModel
  runtime: CardSurfaceRuntime
  instance: Readonly<CardSurfaceInstance>
  decorations: readonly CardSurfaceDecoration[]
}

export function createUnavailableCardCommandManifest(
  reason: CardCapabilityUnavailableReason,
): CardCommandManifest {
  const commands = {} as Record<CardCommandId, CardCommandCapability>
  for (const id of CARD_COMMAND_IDS) {
    commands[id] = { availability: 'unavailable', reason }
  }
  return commands
}
